/**
 * Display helpers for the Data Quality panel.
 *
 * The backend brief fixes the year format as "FY2026" / "FY2017-FY2026", so
 * these deliberately do NOT use the region-aware `formatFiscalYear` from
 * ./formatters (which renders India as "FY26").
 */

/** 2026 → "FY2026". */
export function formatFiscalYear(year: number): string {
  return `FY${year}`;
}

/**
 * Render an issue's affected years: a single year as "FY2026", a contiguous
 * span as "FY2017-FY2026", and anything gappy as the first and last year with
 * an ellipsis so the row never wraps.
 */
export function formatYearRange(years: number[] | null | undefined): string {
  const sorted = [...(years ?? [])].filter((y) => Number.isFinite(y)).sort((a, b) => a - b);
  if (sorted.length === 0) return '';
  if (sorted.length === 1) return formatFiscalYear(sorted[0]);

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const isContiguous = last - first + 1 === sorted.length;

  return isContiguous
    ? `${formatFiscalYear(first)}-${formatFiscalYear(last)}`
    : `${formatFiscalYear(first)}…${formatFiscalYear(last)}`;
}

/** "income_before_tax" → "Income before tax"; "PDF" and "API" pass through. */
export function humanizeFieldName(field: string): string {
  const words = field.replace(/[_-]+/g, ' ').trim();
  if (!words) return field;
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

/** 0.97 → "0.97". The score is the backend's — never recompute it. */
export function formatQualityScore(score: number | null | undefined): string {
  if (score == null || !Number.isFinite(score)) return '';
  return score.toFixed(2);
}

/**
 * The compact year span for a tab label: "FY17–FY26", or "FY24–25" when both
 * years share a century prefix, and a bare "FY24" for a single year.
 *
 * Deliberately shorter than `formatYearRange`. A tab label already carries the
 * source and the score, and "FY2017-FY2026" would push the score off the end
 * of a narrow strip — the point of putting the range there is that both
 * sources' shape is legible at a glance, which a wrapping label defeats.
 *
 * Derived from `summary.periods[].year`; returns "" when no periods were
 * reported, so the caller omits the segment rather than printing an empty one.
 */
export function formatPeriodRange(years: (number | null | undefined)[]): string {
  const sorted = [...(years ?? [])]
    .filter((year): year is number => year != null && Number.isFinite(year))
    .sort((a, b) => a - b);
  if (sorted.length === 0) return '';

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const short = (year: number) => String(year % 100).padStart(2, '0');

  if (first === last) return `FY${short(first)}`;

  // A short span within one decade reads fine as "FY24–25" — the reader
  // carries the decade across two adjacent years without help. A wider span
  // repeats the FY ("FY17–FY26") because "FY17–26" invites reading the second
  // number as a count of years rather than as a year.
  return Math.floor(first / 10) === Math.floor(last / 10)
    ? `FY${short(first)}–${short(last)}`
    : `FY${short(first)}–FY${short(last)}`;
}

/**
 * Which score `quality_score_basis` actually names.
 *
 * One resolver so the gauge, the tab label and the worst-tab default can never
 * disagree about what "the score" is. Two rules, both from the contract:
 *
 *   - Only an explicit "agent_adjusted" basis promotes the adjusted number.
 *     Anything else — absent, "rules", an unrecognised value — leaves the
 *     deterministic score in charge. The backend can compute an adjustment and
 *     still rule that the rule score stands, so the presence of an adjusted
 *     number is never on its own the authority.
 *   - A basis of "agent_adjusted" with no usable adjusted number falls back to
 *     the rule score rather than rendering nothing. A missing number is a
 *     backend inconsistency, not a reason to hide a score the panel holds.
 */
export function resolveScore(summary: {
  quality_score: number;
  quality_score_agent_adjusted?: number | null;
  quality_score_basis?: string | null;
}): { score: number; isAgentAdjusted: boolean } {
  const adjusted = summary.quality_score_agent_adjusted;
  const hasAdjusted = adjusted != null && Number.isFinite(adjusted);

  if (summary.quality_score_basis === 'agent_adjusted' && hasAdjusted) {
    return { score: adjusted as number, isAgentAdjusted: true };
  }
  return { score: summary.quality_score, isAgentAdjusted: false };
}
